/** Playlists + smart-rules (pages/playlists.tsx, playlist-detail.tsx, smart-rules-builder). */
export const playlists = {
  // Shared atoms reused across the playlists surfaces.
  smartStation: "Smart station",
  shufflePlayback: "Shuffle playback",
  optionalDescription: "Optional description",

  // pages/playlists.tsx — list page.
  page: {
    title: "Playlists",
    description: "Manage content for each channel.",
    searchPlaceholder: "Search playlists…",
    playbackPlaceholder: "Playback",
    filterAll: "All playlists",
    filterShuffleOnly: "Shuffle only",
    filterSequentialOnly: "Sequential only",
    newPlaylist: "New Playlist",
    error: {
      title: "Couldn't load playlists",
      description:
        "Something went wrong while loading playlists. Check your connection and try again.",
    },
    presetsHeading: "OmniMedia Presets",
    yourHeading: "Your Playlists",
    presetBadge: "Preset",
    stationBadge: "Station",
    shuffled: "Shuffled",
    sequential: "Sequential",
    noMatches: "No playlists match your filters.",
    emptyChannel: "No playlists for this channel yet.",
    createOne: "Create one",
    emptyTitle: "No playlists yet",
    noMatchTitle: "No matches",
    clearFilters: "Clear filters",
  },

  // Create playlist / station dialog.
  create: {
    manual: "Manual",
    playlistTitle: "Create playlist",
    stationTitle: "Create station",
    forChannel: " for {channel}",
    shuffleHint: "Play items in random order on devices.",
    smartHint:
      "Pick the sound. The station fills itself from the catalogue and updates automatically as it grows.",
    aiLabel: "Describe your station",
    aiPlaceholder: "e.g. laid-back Sunday brunch, no vocals, mid tempo",
    aiDraft: "Draft rules",
    aiDrafting: "Drafting…",
    aiApplied: "Rules drafted. Review and tweak below",
    aiPreviewCount: "{count} tracks match",
    aiDropped: "Not in the catalogue (skipped): {values}",
    aiUnavailable: "AI drafting isn't configured on this server yet.",
    aiFailed: "Couldn't draft rules. Try rewording your description.",
  },

  // Merge playlists dialog.
  merge: {
    title: "Merge playlists",
    namePlaceholder: "New playlist name",
    presetBadge: "preset",
    submit: "Create merged playlist",
  },

  // Toasts + confirms (list page).
  toast: {
    presetsCannotDelete: "System presets cannot be deleted",
    deleteConfirm: "Delete playlist?",
    deleted: "Playlist deleted",
    mergeSelectPrompt: "Select at least 2 playlists and enter a name",
    mergeFailed: "Merge failed",
    unknownError: "Unknown error",
    merged: "Merged playlist created",
    stationCreated: "Station created",
    playlistCreated: "Playlist created",
    createFailed: "Failed to create",
  },

  // pages/playlist-detail.tsx — detail page.
  detail: {
    mobileTitle: "Playlist",
    panelLabel: "Playlist actions",
    notFound: "Playlist not found",
    error: {
      title: "Couldn't load playlist",
      description:
        "Something went wrong while loading this playlist. Check your connection and try again.",
    },
    backToPlaylists: "Back to Playlists",
    channelLabel: "Channel: {name} ({type})",
    emptySmart:
      "No catalogue tracks match this station's rules yet. Broaden the rules, or wait for the catalogue to grow.",
    emptyManual: "This playlist is empty. Add some tracks or media.",
    emptyManualTitle: "This playlist is empty",
    emptyManualDesc: "Add tracks from the catalogue or your own media.",
    emptySmartTitle: "No matching tracks yet",
    randomOrder: "Random order on devices",
    shuffleEnabled: "Shuffle enabled",
    sequentialPlayback: "Sequential playback",
    addItem: "Add Item",
    addDialogTitle: "Add to Playlist",
    tabCatalogue: "Music Catalogue",
    tabMedia: "My Media",
    searchTracksPlaceholder: "Search tracks by title, artist, genre…",
    searchMediaPlaceholder: "Search media…",
    added: "Added",
    noTracksFound: "No tracks found",
    loadMore: "Load more ({count} of {total})",
    noReadyMedia: "No ready {purpose} media found",
    trackFallback: "Track {id}",
    assetFallback: "Asset {id}",
    toast: {
      itemRemoved: "Item removed",
      trackAdded: "Track added",
      addTrackFailed: "Failed to add track",
      assetAdded: "Asset added",
      addAssetFailed: "Failed to add asset",
      reorderFailed: "Failed to reorder",
      updated: "Playlist updated",
      updateFailed: "Failed to update",
    },
  },

  // Smart station rules card (playlist-detail.tsx).
  station: {
    editRules: "Edit rules",
    editRulesTitle: "Edit station rules",
    saveRules: "Save rules",
    explainer:
      "The track list is picked from the catalogue by these rules and updates automatically as the catalogue grows.",
    toast: {
      updated: "Station updated",
      updatedDescription: "Track list refreshed from the catalogue.",
      updateFailed: "Failed to update station",
    },
  },

  // Smart rules builder (components/playlists/smart-rules-builder.tsx).
  rules: {
    genres: "Genres",
    moods: "Moods",
    venues: "Venues",
    tempo: "Tempo",
    vocals: "Vocals",
    maxTracks: "Max tracks",
    energy: "Energy",
    any: "Any",
    range: "{min} – {max}",
    energyHint: "1 = calm background, 5 = peak-hour push",
    estimating: "Estimating…",
    tracksLabel: "tracks",
    fromMatches: "· from {count} matches",
    lowPool: {
      one: "Only {count} matching track. A station needs at least {min} and stays empty until the catalogue grows.",
      other:
        "Only {count} matching tracks. A station needs at least {min} and stays empty until the catalogue grows.",
    },
  },

  // Compact rules summary badges (smart-rules-builder.tsx).
  summary: {
    tempo: "{tempo} tempo",
    energy: "energy {min}–{max}",
    upToTracks: "up to {count} tracks",
    wholeCatalogue: "Whole catalogue",
  },
} as const;
